import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { BrainCircuit, Loader2, TrendingUp, TrendingDown, Coins, Target } from "lucide-react";
import { scoreColor } from "@/components/Gauge";
import { getInsights } from "@/lib/api";

const fmtUsageByUnit = (byUnit) => {
  if (!byUnit) return null;
  const parts = [];
  if (byUnit.credits) parts.push(`~${Math.round(byUnit.credits)} cr`);
  if (byUnit.tokens) {
    const t = byUnit.tokens;
    parts.push(t >= 1000 ? `~${Math.round(t / 1000)}K tok` : `~${Math.round(t)} tok`);
  }
  if (byUnit.usd) parts.push(`~$${byUnit.usd.toFixed(2)}`);
  return parts.length ? parts.join(" · ") : null;
};

export const PersonalizedIntelligence = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    getInsights().then(setData).catch(() => setData({ count: 0 }));
  }, []);

  const Header = (
    <div className="flex items-start gap-3 mb-6">
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-neon-cyan/50 glow-cyan shrink-0">
        <BrainCircuit size={20} className="text-neon-cyan" />
      </span>
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-white">
          Personalized Intelligence
        </h2>
        <p className="font-mono text-sm text-[var(--muted)] mt-1 max-w-xl leading-relaxed">
          With your authorization, Coretext learns from your previous builds and prompt
          history to estimate the usage you avoid and the gaps that cost you most.
        </p>
      </div>
    </div>
  );

  return (
    <section className="glass neon-border rounded-2xl p-6 md:p-7 mb-8" data-testid="personalized-intelligence">
      {Header}

      {data === null ? (
        <div className="flex items-center gap-3 font-mono text-sm text-neon-cyan py-6">
          <Loader2 className="animate-spin" size={18} /> Learning your patterns...
        </div>
      ) : data.count === 0 ? (
        <p className="font-mono text-sm text-[var(--muted)] leading-relaxed" data-testid="intelligence-empty-state">
          CoreText needs more of your build history before it can identify your recurring
          correction patterns. Run a few audits (or connect your build history below) and your
          personal patterns — typical readiness, the checks you most often miss, and the usage
          you avoid — will appear here. Nothing here is generated from demos or other users.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <Stat
              testid="insight-usage-avoided"
              label="USAGE AVOIDED"
              value={fmtUsageByUnit(data.usage_by_unit) || "No estimate"}
              sub={data.usage_estimate_available ? "native units, across audits" : "not enough data to estimate"}
              color="#22e58a"
              icon={Coins}
            />
            <Stat
              label="AVG READINESS"
              value={data.avg_score}
              sub={`typically → ${data.avg_projected}`}
              color={scoreColor(data.avg_score)}
              icon={Target}
            />
            <Stat
              label="AVG REBUILD LOOPS"
              value={data.avg_loops}
              sub="avoided / prompt"
              color="#22d3ee"
              icon={BrainCircuit}
            />
            <Stat
              label="TREND"
              value={data.trend === null ? "—" : `${data.trend >= 0 ? "+" : ""}${data.trend}`}
              sub={data.trend === null ? "need 4+ audits" : "vs your early prompts"}
              color={data.trend >= 0 ? "#22e58a" : "#ff3b5c"}
              icon={data.trend >= 0 ? TrendingUp : TrendingDown}
            />
          </div>

          {data.top_gaps?.length > 0 && (
            <div className="mt-6">
              <p className="font-mono text-[11px] tracking-[0.25em] text-[var(--muted)] mb-3">
                YOUR MOST COMMON GAPS{data.top_platform ? ` · MOSTLY ON ${data.top_platform.toUpperCase()}` : ""}
              </p>
              <div className="space-y-2.5">
                {data.top_gaps.map((g) => (
                  <div key={g.key} className="flex items-center gap-3" data-testid={`insight-gap-${g.key}`}>
                    <span className="font-mono text-xs text-white w-44 shrink-0 truncate">{g.label}</span>
                    <div className="relative h-2 flex-1 rounded-full bg-[#0b0d14] overflow-hidden border border-[var(--line)]">
                      <motion.div
                        initial={{ width: 0 }} whileInView={{ width: `${g.pct}%` }} viewport={{ once: true }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute inset-y-0 left-0 rounded-full"
                        style={{ background: "#ff3b5c", boxShadow: "0 0 12px #ff3b5c88" }}
                      />
                    </div>
                    <span className="font-mono text-xs text-neon-red w-12 text-right">{g.pct}%</span>
                  </div>
                ))}
              </div>
              <p className="font-mono text-[11px] text-[var(--muted)] mt-4">
                Fixing these upfront is where your avoided usage climbs fastest.
                As you run more audits, CoreText sharpens this analysis from your own CoreText activity.
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
};

const Stat = ({ label, value, sub, color, icon: Icon, testid }) => (
  <div className="glass rounded-xl p-3 sm:p-4" data-testid={testid}>
    <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.15em] text-[var(--muted)] flex items-center gap-1.5">
      <Icon size={12} style={{ color }} className="shrink-0" /> {label}
    </p>
    <p className="font-display text-2xl sm:text-3xl font-bold mt-1" style={{ color, textShadow: `0 0 16px ${color}55` }}>
      {value}
    </p>
    <p className="font-mono text-[10px] text-[var(--muted)] mt-0.5">{sub}</p>
  </div>
);
